// src/components/chat/ConcertinaThread.jsx
//
// Shared concertina thread engine — one headline-optional engine, skinned by `variant`.
// Extracted from the ChatColumn v5 inline accordion so locality Chat AND article/event
// comments render through a SINGLE code path: a fix to engagement, hybrid delete, the
// is_deleted_by_author gravestone, the edited marker, or the reply tree lands everywhere.
//
// The differences between contexts are expressed ONLY as a `variant` config lookup
// (headline ownership + skin + load strategy), never as a forked JSX tree. The shared
// message renderer, handlers, and the delete path (chatService.deleteChat) are identical
// in every variant.
//
//   variant 'chat'    — locality column: self-owned headline, boxed card, full width,
//                       single-open accordion, lazy per-thread load, #/chat/{uuid} permalink.
//   variant 'comment' — article/event comments: inherited headline (parent supplies the
//                       header), text-only (no per-comment box), narrower, all loaded at once.
//   variant 'group'   — RESERVED for a future group-chat adoption of this engine. The config
//                       map below is open-ended; add a `group` entry, do not reshape the seam.
//
// Data access is injected by the consumer via callbacks (loadResponses / onCreateTopLevel /
// onCreateReply / onRefresh) so the engine owns interaction + render, not the context-specific
// fetch/insert wiring (locality home_locality_id, article_id, group community_id, etc.).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { Flag, Pencil, Trash2 } from 'lucide-react';
import { formatTimeAgo } from '../../utils/formatText';
import { chatService, userActionService } from '../../data/dataService';
import { tokens } from '../../styles/designTokens';
import ConfirmationModal from '../core/ConfirmationModal';
import ReportModal from '../shared/ReportModal';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';
import { isGroupClosingError, GROUP_CLOSING_MESSAGE_KEY } from '../../utils/groupClosingError';

// --- Variant configuration map (extensible; add `group` here later, do not fork render) ---
const VARIANTS = {
  chat: {
    headlineMode: 'self',      // own headline list; the thread supplies its own title
    collapsible: true,         // single-open accordion of headline rows
    skin: 'boxed',             // white card per thread
    composerMode: 'toggle',    // new-thread composer hidden until the + button
    loadStrategy: 'lazy',      // responses fetched per-thread on open
    permalink: true,           // #/chat/{uuid} auto-expand + history
    requireHeadline: true,     // top-level posts need a headline
    minBodyLength: 10,
    listenComposerEvent: true, // honours ournet:open-chat-composer (mobile create pill)
    maxWidth: null,            // fills the column
  },
  comment: {
    headlineMode: 'inherited', // no headline — the article/event header is the title
    collapsible: false,        // every comment + its replies render expanded
    skin: 'plain',             // text on the page, no card/box per comment
    composerMode: 'inline',    // a "write a comment" box is always present
    loadStrategy: 'eager',     // all comments arrive in one fetch from the consumer
    permalink: false,
    requireHeadline: false,
    minBodyLength: 1,
    listenComposerEvent: false,
    maxWidth: tokens.commentThread?.maxWidth || null,
  },
};

// --- Slide-down wrapper (respects prefers-reduced-motion) ---
// After the open animation completes, switches to maxHeight:'none' so that
// nested SlideDown components (e.g. reply composers inside thread panels)
// can expand without being clipped by the parent's fixed pixel height.
const SlideDown = ({ open, children }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [settled, setSettled] = useState(false);
  const prevOpenRef = useRef(open);
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (prevOpenRef.current !== open) {
      setSettled(false);
      prevOpenRef.current = open;
    }
  }, [open]);

  useEffect(() => {
    if (open && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open, children]);

  if (reducedMotion.current) {
    return open ? <div>{children}</div> : null;
  }

  return (
    <div
      style={{
        maxHeight: open ? (settled ? 'none' : `${height}px`) : '0px',
        opacity: open ? 1 : 0,
        overflow: settled ? 'visible' : 'hidden',
        transition: settled ? 'none' : 'max-height 0.25s ease, opacity 0.2s ease'
      }}
      onTransitionEnd={() => { if (open) setSettled(true); }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

const ConcertinaThread = ({
  variant = 'chat',
  data = [],                       // top-level items (threads for chat, top-level comments for comment)
  loadResponses,                   // async (rootId) => responses[]  (flat descendants, engine-shaped)
  onCreateTopLevel,                // async ({ headline, content }) => created item (throws on failure)
  onCreateReply,                   // async ({ parentChatId, rootChatId, content }) => created item
  onRefresh,                       // () => void  — re-fetch top-level data after a mutation
  openThreadId: externalOpenThreadId = null, // permalink auto-expand (chat)
  onThreadToggle,                  // (id|null) => void  (chat)
  reportContentType = 'chat',
  emptyState = null,               // node shown when there are no items
}) => {
  const cfg = VARIANTS[variant] || VARIANTS.chat;
  const theme = useTheme();
  const { t } = useTranslation('chat');
  const { t: tUi, formattingLocale } = useTranslation('ui');
  const { t: tCommon } = useTranslation('common');
  const { requireAuth, isLoggedIn, currentUser } = useAuth();

  // --- State ---
  const [composerOpen, setComposerOpen] = useState(cfg.composerMode === 'inline');
  const [openThreadId, setOpenThreadId] = useState(externalOpenThreadId);
  const [threadResponses, setThreadResponses] = useState({});
  const [loadingThreadId, setLoadingThreadId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);

  // Composer form state
  const [newHeadline, setNewHeadline] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [composerErrors, setComposerErrors] = useState({});

  // Like/dislike state
  const [userLikeStatuses, setUserLikeStatuses] = useState({});
  const [messageCounts, setMessageCounts] = useState({});

  // Modal state
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTargetId, setReportTargetId] = useState(null);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editHeadline, setEditHeadline] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editErrors, setEditErrors] = useState({});

  // Delete confirmation state
  const [deleteModal, setDeleteModal] = useState({ visible: false, chatId: null });

  // Discard guard state
  const [discardModal, setDiscardModal] = useState({ visible: false, pendingAction: null });

  // Refs
  const scrollContainerRef = useRef(null);
  const threadRefs = useRef({});

  // Sync external openThreadId prop (permalink auto-expand) — chat only
  useEffect(() => {
    if (!cfg.permalink) return;
    if (externalOpenThreadId && externalOpenThreadId !== openThreadId) {
      setOpenThreadId(externalOpenThreadId);
      loadResponsesInto(externalOpenThreadId);
    }
  }, [externalOpenThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Load responses for a thread/comment into cache (+ like/view tracking) ---
  const loadResponsesInto = useCallback(async (rootId) => {
    if (threadResponses[rootId]) return; // already cached
    if (!loadResponses) return;
    setLoadingThreadId(rootId);
    try {
      const responses = await loadResponses(rootId);
      setThreadResponses(prev => ({ ...prev, [rootId]: responses || [] }));

      if (currentUser?.id) {
        const allIds = [rootId, ...(responses || []).map(r => r.id)];
        // Fire-and-forget view tracking — fully decoupled, never blocks UI
        allIds.forEach(id => {
          userActionService.trackMessageEngagement(currentUser.id, id, 'view')
            .catch(err => console.warn('engagement tracking failed', err));
        });
        try {
          const statuses = {};
          const counts = {};
          await Promise.all(
            allIds.map(async (id) => {
              const [likeStatus, msgCounts] = await Promise.all([
                userActionService.getMessageLikeDislike(currentUser.id, id),
                userActionService.getMessageCounts(id)
              ]);
              statuses[id] = likeStatus;
              counts[id] = msgCounts;
            })
          );
          setUserLikeStatuses(prev => ({ ...prev, ...statuses }));
          setMessageCounts(prev => ({ ...prev, ...counts }));
        } catch (engErr) {
          console.warn('Failed to load like/count data:', engErr);
        }
      }
    } catch (error) {
      console.error('Error loading thread responses:', error);
    } finally {
      setLoadingThreadId(null);
    }
  }, [threadResponses, currentUser, loadResponses]);

  // Eager variants (comments) load every top-level item's responses up front, since
  // there is no collapse step — each comment renders expanded with its replies visible.
  useEffect(() => {
    if (cfg.loadStrategy !== 'eager') return;
    (data || []).forEach(item => {
      if (!item.parent_chat_id && !threadResponses[item.id]) {
        loadResponsesInto(item.id);
      }
    });
  }, [data, cfg.loadStrategy]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Close thread helper (shared by toggle, click-outside, discard) ---
  const closeThread = useCallback((threadId) => {
    setOpenThreadId(null);
    setReplyingTo(null);
    setReplyBody('');
    onThreadToggle?.(null);
    if (cfg.permalink && window.location.hash.includes(threadId)) {
      window.history.pushState(null, '', window.location.pathname);
    }
  }, [onThreadToggle, cfg.permalink]);

  // --- Open thread helper ---
  const openThread = useCallback((threadId) => {
    setOpenThreadId(threadId);
    setReplyingTo(null);
    loadResponsesInto(threadId);
    onThreadToggle?.(threadId);
    if (cfg.permalink) {
      const newHash = `#/chat/${threadId}`;
      if (window.location.hash !== newHash) {
        window.history.pushState(null, '', newHash);
      }
    }
    // Scroll the just-opened thread into view (user-click path only — see permalink note).
    setTimeout(() => {
      threadRefs.current[threadId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 100);
  }, [loadResponsesInto, onThreadToggle, cfg.permalink]);

  // --- Discard guard ---
  const replyBodyRef = useRef(replyBody);
  replyBodyRef.current = replyBody;
  const editBodyRef = useRef(editBody);
  editBodyRef.current = editBody;
  const editingIdRef = useRef(editingId);
  editingIdRef.current = editingId;

  const guardedClose = useCallback((currentThreadId, thenOpenThreadId = null) => {
    const hasReplyChanges = replyBodyRef.current.trim().length > 0;
    const hasEditChanges = editingIdRef.current != null;
    if (hasReplyChanges || hasEditChanges) {
      setDiscardModal({
        visible: true,
        pendingAction: () => {
          if (hasEditChanges) handleCancelEdit();
          closeThread(currentThreadId);
          if (thenOpenThreadId) openThread(thenOpenThreadId);
        }
      });
    } else {
      closeThread(currentThreadId);
      if (thenOpenThreadId) openThread(thenOpenThreadId);
    }
  }, [closeThread, openThread]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Toggle thread open/close (collapsible variants only) ---
  const toggleThread = useCallback((threadId) => {
    if (openThreadId === threadId) {
      guardedClose(threadId);
    } else if (openThreadId) {
      guardedClose(openThreadId, threadId);
    } else {
      openThread(threadId);
    }
  }, [openThreadId, guardedClose, openThread]);

  // --- Click-outside listener (collapsible variants only) ---
  const modalsOpenRef = useRef(false);
  modalsOpenRef.current = discardModal.visible || modal.visible || showReportModal || deleteModal.visible;

  useEffect(() => {
    if (!cfg.collapsible || !openThreadId) return;

    const handleMouseDown = (e) => {
      if (modalsOpenRef.current) return;
      const openRef = threadRefs.current[openThreadId];
      if (!openRef) return;
      if (openRef.contains(e.target)) return;
      for (const [id, ref] of Object.entries(threadRefs.current)) {
        if (id !== openThreadId && ref && ref.contains(e.target)) return;
      }
      guardedClose(openThreadId);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openThreadId, guardedClose, cfg.collapsible]);

  // --- Like/Dislike handler ---
  const handleLikeDislike = async (messageId, actionType) => {
    if (!currentUser?.id) {
      setModal({
        visible: true,
        message: t('auth:login_to_like'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }
    try {
      const prevStatus = userLikeStatuses[messageId];
      const result = await userActionService.setMessageLikeDislike(currentUser.id, messageId, actionType);
      if (result.success) {
        setUserLikeStatuses(prev => ({ ...prev, [messageId]: result.newStatus }));
        setMessageCounts(prev => {
          const current = prev[messageId] || { likeCount: 0, dislikeCount: 0 };
          let likeCount = current.likeCount;
          let dislikeCount = current.dislikeCount;
          if (prevStatus === 'like' && result.newStatus !== 'like') likeCount = Math.max(0, likeCount - 1);
          if (prevStatus !== 'like' && result.newStatus === 'like') likeCount++;
          if (prevStatus === 'dislike' && result.newStatus !== 'dislike') dislikeCount = Math.max(0, dislikeCount - 1);
          if (prevStatus !== 'dislike' && result.newStatus === 'dislike') dislikeCount++;
          return { ...prev, [messageId]: { likeCount, dislikeCount } };
        });
      }
    } catch (error) {
      console.error('Error saving like/dislike:', error);
    }
  };

  // --- Engagement score for response sorting ---
  const calculateEngagementScore = (chat) => {
    const likeScore = (chat.like_count || 0) - (chat.dislike_count || 0);
    const ageInHours = (Date.now() - new Date(chat.created_at)) / (1000 * 60 * 60);
    const timeFactor = Math.exp(-ageInHours / 24);
    return likeScore + (timeFactor * 2);
  };

  // --- Build threaded + sorted responses, clamped to indent level 1 ---
  const buildFlattenedResponses = (responses, rootId) => {
    if (!responses || responses.length === 0) return [];
    const byParent = {};
    for (const r of responses) {
      const key = r.parent_chat_id || rootId;
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(r);
    }
    for (const key of Object.keys(byParent)) {
      byParent[key].sort((a, b) => calculateEngagementScore(b) - calculateEngagementScore(a));
    }
    const result = [];
    const directReplies = byParent[rootId] || [];
    for (const reply of directReplies) {
      result.push({ ...reply, indentLevel: 0 });
      const subReplies = byParent[reply.id] || [];
      for (const sub of subReplies) {
        result.push({ ...sub, indentLevel: 1 });
        const deeperReplies = byParent[sub.id] || [];
        for (const deep of deeperReplies) {
          result.push({ ...deep, indentLevel: 1 });
        }
      }
    }
    return result;
  };

  // --- New top-level composer submit ---
  const handleCreateTopLevel = async () => {
    const errors = {};
    if (cfg.requireHeadline && (!newHeadline || newHeadline.trim().length < 3)) {
      errors.headline = t('error_headline_too_short');
    }
    if (!newBody || newBody.trim().length < cfg.minBodyLength) {
      errors.body = t('error_content_too_short');
    }
    if (Object.keys(errors).length > 0) {
      setComposerErrors(errors);
      return;
    }

    setSaving(true);
    try {
      await onCreateTopLevel({
        // Saved exactly as typed (#93): safety is at display.
        headline: cfg.requireHeadline ? newHeadline.trim() : null,
        content: newBody.trim()
      });
      setNewHeadline('');
      setNewBody('');
      setComposerErrors({});
      if (cfg.composerMode === 'toggle') setComposerOpen(false);
      if (cfg.requireHeadline) {
        setModal({
          visible: true,
          message: t('success_chat_created'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
      onRefresh?.();
    } catch (error) {
      surfaceMutationError(error);
    } finally {
      setSaving(false);
    }
  };

  // --- Reply composer submit ---
  const handlePostReply = async (parentChatId, rootChatId) => {
    if (!replyBody || replyBody.trim().length < 1) return;
    setSaving(true);
    try {
      await onCreateReply({
        parentChatId,
        rootChatId,
        content: replyBody.trim()
      });
      setReplyBody('');
      setReplyingTo(null);
      // Refresh the thread's responses cache from the consumer's loader
      if (loadResponses) {
        const updated = await loadResponses(rootChatId);
        setThreadResponses(prev => ({ ...prev, [rootChatId]: updated || [] }));
      }
      onRefresh?.();
    } catch (error) {
      surfaceMutationError(error);
    } finally {
      setSaving(false);
    }
  };

  // --- Shared error surfacing for create/reply/edit ---
  // Adapters may attach `error.userMessage` to surface a context-specific, already-localized
  // message (e.g. "no locality resolved") verbatim instead of the generic save-failure wrap.
  const surfaceMutationError = (error) => {
    const isSessionExpired = error?.code === 'SESSION_EXPIRED' || error?.message === 'SESSION_EXPIRED';
    // 42501: the database refused a write where the writer cannot read (not a member, blocked,
    // or content they cannot see). One neutral message — never which (6 Sep 2026 ruling).
    const isRefused = error?.code === '42501' || /"code"\s*:\s*"42501"/.test(error?.message || '');
    const msg = error?.userMessage
      ? error.userMessage
      : isSessionExpired
        ? t('chat:session_expired')
        : isRefused
          ? t('chat:not_able_to_post_here')
          : isGroupClosingError(error)
            ? t(GROUP_CLOSING_MESSAGE_KEY)
          : isRateLimited(error)
            ? rateLimitMessage(error, t)
            : t('error_failed_to_save', { error: error?.message || '' });
    setModal({
      visible: true,
      title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
      message: msg,
      onOk: () => {
        setModal({ visible: false, message: '', onOk: null });
        if (isSessionExpired) {
          window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', { detail: { message: msg } }));
        }
      }
    });
  };

  // --- Composer open trigger (toggle variants) ---
  const handleOpenComposer = () => {
    requireAuth('gate_body_create_chat', () => {
      setComposerOpen(prev => !prev);
    });
  };

  // Listen for external composer open request (mobile header create pill) — chat only
  useEffect(() => {
    if (!cfg.listenComposerEvent) return;
    const handler = () => handleOpenComposer();
    window.addEventListener('ournet:open-chat-composer', handler);
    return () => window.removeEventListener('ournet:open-chat-composer', handler);
  }, [cfg.listenComposerEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Edit handlers ---
  const handleStartEdit = (chat) => {
    setEditingId(chat.id);
    setEditHeadline(chat.headline || '');
    setEditBody(chat.content || '');
    setEditErrors({});
    setReplyingTo(null);
    setReplyBody('');
  };

  const handleSaveEdit = async (chatId, isTopLevel) => {
    const errors = {};
    const headlineEdited = isTopLevel && cfg.requireHeadline;
    if (headlineEdited && (!editHeadline || editHeadline.trim().length < 3)) {
      errors.headline = t('error_headline_too_short');
    }
    const minBody = isTopLevel ? cfg.minBodyLength : 1;
    if (!editBody || editBody.trim().length < minBody) {
      errors.body = t('error_content_too_short');
    }
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const updatePayload = { chatId, content: editBody.trim() }; // as typed (#93)
      if (headlineEdited) {
        updatePayload.headline = editHeadline.trim();
      }
      await chatService.updateChat(updatePayload);

      setEditingId(null);
      setEditHeadline('');
      setEditBody('');
      setEditErrors({});

      onRefresh?.();
      // Refresh the responses cache for whichever thread holds this row
      const rootItem = data.find(c => c.id === chatId);
      if (rootItem && loadResponses) {
        const updated = await loadResponses(rootItem.id);
        setThreadResponses(prev => ({ ...prev, [rootItem.id]: updated || [] }));
      } else if (loadResponses) {
        for (const [threadId, responses] of Object.entries(threadResponses)) {
          if (responses.some(r => r.id === chatId)) {
            const updated = await loadResponses(threadId);
            setThreadResponses(prev => ({ ...prev, [threadId]: updated || [] }));
            break;
          }
        }
      }
    } catch (error) {
      setModal({
        visible: true,
        message: t('error_failed_to_save', { error: error?.message || '' }),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditHeadline('');
    setEditBody('');
    setEditErrors({});
  };

  // --- Delete handler (hybrid soft/hard via chatService.deleteChat — shared path) ---
  const handleConfirmDelete = async () => {
    const chatId = deleteModal.chatId;
    if (!chatId) return;
    setDeleteModal({ visible: false, chatId: null });

    setSaving(true);
    try {
      const result = await chatService.deleteChat({ chatId });

      if (result.action === 'hard_deleted') {
        setThreadResponses(prev => {
          const updated = { ...prev };
          delete updated[chatId];
          for (const [threadId, responses] of Object.entries(updated)) {
            if (responses.some(r => r.id === chatId)) {
              updated[threadId] = responses.filter(r => r.id !== chatId);
            }
          }
          return updated;
        });
      }

      onRefresh?.();

      if (result.action === 'soft_deleted' && loadResponses) {
        for (const [threadId, responses] of Object.entries(threadResponses)) {
          if (threadId === chatId || responses.some(r => r.id === chatId)) {
            const key = threadId === chatId ? chatId : threadId;
            const updated = await loadResponses(key);
            setThreadResponses(prev => ({ ...prev, [key]: updated || [] }));
            break;
          }
        }
      }
    } catch (error) {
      setModal({
        visible: true,
        message: t('error_failed_to_save', { error: error?.message || '' }),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setSaving(false);
    }
  };

  // ======================================================================
  // Shared message block — used for ALL replies and for comment top-levels.
  // One renderer: deleted gravestone / inline edit / normal (meta+body+actions+composer).
  // ======================================================================
  const renderMessageBlock = (msg, rootChatId, { indent = 0, topLevel = false } = {}) => {
    const marginLeftPx = topLevel ? 0 : (indent + 1) * 20;
    const showLeftBorder = !topLevel && indent > 0;
    const isDeleted = msg.is_deleted_by_author;
    const isOwn = currentUser?.id && msg.author_id === currentUser.id;
    const isEditing = editingId === msg.id;

    // Deleted gravestone — kept as a thread anchor, distinct from is_author_removed.
    if (isDeleted) {
      return (
        <div key={msg.id} style={{ marginLeft: `${marginLeftPx}px` }}>
          <div style={{
            padding: '6px 10px',
            borderBottom: '1px solid #f3f4f6',
            borderLeft: showLeftBorder ? `2px solid ${theme.primary}30` : 'none'
          }}>
            <div style={{
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px',
              color: '#9CA3AF',
              fontStyle: 'italic',
              lineHeight: '1.4'
            }}>
              {t('deleted_placeholder')}
            </div>
          </div>
        </div>
      );
    }

    // Inline edit mode
    if (isEditing) {
      return (
        <div key={msg.id} style={{ marginLeft: `${marginLeftPx}px` }}>
          <div style={{
            padding: '8px 10px',
            background: '#f0f7ff',
            borderBottom: '1px solid #e5e7eb',
            borderLeft: showLeftBorder ? `2px solid ${theme.primary}30` : 'none',
            border: `1px solid ${theme.primary}30`
          }}>
            <textarea
              value={editBody}
              onChange={(e) => { setEditBody(e.target.value); setEditErrors(prev => ({ ...prev, body: null })); }}
              maxLength={5000}
              style={{
                width: '100%', minHeight: '50px',
                border: editErrors.body ? '2px solid #EF4444' : '1px solid #e5e7eb',
                borderRadius: '6px', padding: '6px 8px',
                fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px',
                resize: 'vertical', outline: 'none', boxSizing: 'border-box'
              }}
              autoFocus
            />
            {editErrors.body && (
              <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '2px' }}>{editErrors.body}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
              <button onClick={handleCancelEdit} style={btnSecondary}>{tCommon('cancel')}</button>
              <button onClick={() => handleSaveEdit(msg.id, topLevel)} disabled={saving} style={btnConfirm(saving)}>
                {t('chat:save_exit')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={msg.id} style={{ marginLeft: `${marginLeftPx}px` }}>
        <div style={{
          padding: '6px 10px',
          borderBottom: '1px solid #f3f4f6',
          borderLeft: showLeftBorder ? `2px solid ${theme.primary}30` : 'none'
        }}>
          {/* Meta line: author+time LEFT, engagement RIGHT */}
          <div style={metaLineStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontWeight: '600',
                color: msg.is_author_removed ? '#9CA3AF' : theme.primary,
                fontStyle: msg.is_author_removed ? 'italic' : 'normal'
              }}>
                {msg.is_author_removed ? tCommon('removed_author') : (msg.author_name || msg.author_id)}
              </span>
              <span>{formatTimeAgo(msg.created_at, tUi, formattingLocale)}</span>
              {msg.edited_at && (
                <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>{t('edited_marker')}</span>
              )}
            </div>
            {renderEngagement(msg.id)}
          </div>

          {/* Body */}
          <div style={{
            fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px',
            color: '#374151', lineHeight: '1.4', marginBottom: '4px'
          }}>
            {msg.content}
          </div>

          {/* Action row: Reply + Edit/Delete (own) + Report */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => {
                  requireAuth('gate_body_comment', () => {
                    setReplyingTo(msg.id);
                    setReplyBody('');
                  });
                }}
                style={btnReply}
              >
                {t('reply')}
              </button>
              {isOwn && (
                <>
                  <button onClick={() => handleStartEdit(msg)} title={t('edit')} style={iconBtn('#6b7280')}>
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => setDeleteModal({ visible: true, chatId: msg.id })} title={t('delete')} style={iconBtn('#DC2626')}>
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
            <button
              onClick={() => {
                if (!currentUser?.id) { requireAuth('gate_body_generic'); return; }
                setReportTargetId(msg.id);
                setShowReportModal(true);
              }}
              style={iconBtn('#DC2626')}
            >
              <Flag size={11} />
            </button>
          </div>
        </div>

        {/* Inline reply composer (children mount only while open to avoid stray autofocus) */}
        <SlideDown open={replyingTo === msg.id}>
          {replyingTo === msg.id && (
            <div style={{ padding: '8px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={msg.is_author_removed
                  ? `${tCommon('removed_author')}...`
                  : t('respond_to_placeholder', { name: msg.author_name || t('this_chat') })}
                style={composerTextareaStyle(false)}
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                <button onClick={() => { setReplyingTo(null); setReplyBody(''); }} style={btnSecondary}>{tCommon('cancel')}</button>
                <button onClick={() => handlePostReply(msg.id, rootChatId)} disabled={saving} style={btnConfirm(saving)}>
                  {t('chat:save_exit')}
                </button>
              </div>
            </div>
          )}
        </SlideDown>
      </div>
    );
  };

  // --- Engagement (like/dislike) cluster — shared ---
  const renderEngagement = (id) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span onClick={() => handleLikeDislike(id, 'like')}
        style={{ cursor: 'pointer', fontSize: '11px', opacity: userLikeStatuses[id] === 'like' ? 1 : 0.4 }}>
        {'👍'}
      </span>
      <span style={{ color: '#10B981', fontSize: '10px' }}>{messageCounts[id]?.likeCount || 0}</span>
      <span onClick={() => handleLikeDislike(id, 'dislike')}
        style={{ cursor: 'pointer', fontSize: '11px', opacity: userLikeStatuses[id] === 'dislike' ? 1 : 0.4 }}>
        {'👎'}
      </span>
      {(messageCounts[id]?.dislikeCount || 0) > 0 && (
        <span style={{ color: '#EF4444', fontSize: '10px' }}>{messageCounts[id]?.dislikeCount}</span>
      )}
    </div>
  );

  // ======================================================================
  // Collapsible thread (headline self-mode — locality Chat / future group)
  // ======================================================================
  const renderCollapsibleThread = (chat) => {
    const isOpen = openThreadId === chat.id;
    const responses = threadResponses[chat.id] || [];
    const flatResponses = buildFlattenedResponses(responses, chat.id);
    const isLoading = loadingThreadId === chat.id;
    const isDeleted = chat.is_deleted_by_author;
    const isOwn = currentUser?.id && chat.author_id === currentUser.id;
    const isEditing = editingId === chat.id;

    return (
      <div
        key={chat.id}
        ref={el => { threadRefs.current[chat.id] = el; }}
        style={{ marginBottom: '4px', background: 'white', borderRadius: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', overflow: 'hidden' }}
      >
        {/* Headline row — always visible */}
        <div
          onClick={() => toggleThread(chat.id)}
          style={{ padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background-color 0.1s' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {/* Thread-starter marker — context-coloured (theme.primary), distinguishes a
              top-level post from its reply rows. Replies carry no headline and no dot. */}
          <span aria-hidden="true" style={{
            width: '6px', height: '6px', borderRadius: '50%',
            background: theme.primary, marginRight: '8px', flexShrink: 0, display: 'inline-block'
          }} />
          <div style={{
            flex: 1, fontFamily: 'OpenSauceSans, sans-serif', fontWeight: '600', fontSize: '13px',
            color: isDeleted ? '#9CA3AF' : '#1f2937', fontStyle: isDeleted ? 'italic' : 'normal',
            lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {isDeleted ? t('deleted_placeholder') : (chat.headline || chat.content)}
          </div>
          <span style={{
            fontSize: '10px', color: '#9ca3af', marginLeft: '6px', transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', flexShrink: 0
          }}>
            {'▶'}
          </span>
        </div>

        {/* Expanded panel */}
        <SlideDown open={isOpen}>
          <div style={{ borderTop: '1px solid #f0f0f0' }}>
            {isDeleted ? (
              <div style={{ padding: '10px', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', lineHeight: '1.5' }}>
                {t('deleted_placeholder')}
              </div>
            ) : isEditing ? (
              <div style={{ padding: '10px', background: '#f0f7ff', border: `1px solid ${theme.primary}30` }}>
                <input
                  type="text"
                  value={editHeadline}
                  onChange={(e) => { setEditHeadline(e.target.value); setEditErrors(prev => ({ ...prev, headline: null })); }}
                  maxLength={300}
                  style={{
                    width: '100%', padding: '6px 8px', fontSize: '13px', fontFamily: 'OpenSauceSans, sans-serif',
                    border: editErrors.headline ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px',
                    outline: 'none', marginBottom: editErrors.headline ? '2px' : '6px', boxSizing: 'border-box'
                  }}
                  autoFocus
                />
                {editErrors.headline && (
                  <div style={{ fontSize: '11px', color: '#EF4444', marginBottom: '4px' }}>{editErrors.headline}</div>
                )}
                <textarea
                  value={editBody}
                  onChange={(e) => { setEditBody(e.target.value); setEditErrors(prev => ({ ...prev, body: null })); }}
                  maxLength={5000}
                  style={{
                    width: '100%', minHeight: '60px', padding: '6px 8px', fontSize: '13px', fontFamily: 'OpenSauceSans, sans-serif',
                    border: editErrors.body ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px',
                    outline: 'none', resize: 'vertical', marginBottom: editErrors.body ? '2px' : '6px', boxSizing: 'border-box'
                  }}
                />
                {editErrors.body && (
                  <div style={{ fontSize: '11px', color: '#EF4444', marginBottom: '4px' }}>{editErrors.body}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={handleCancelEdit} style={btnSecondaryWide}>{tCommon('cancel')}</button>
                  <button onClick={() => handleSaveEdit(chat.id, true)} disabled={saving} style={btnPrimaryWide(theme, saving)}>
                    {t('chat:save_exit')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Root meta line */}
                <div style={{ padding: '6px 10px 0', ...metaLineStyle, fontSize: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontWeight: '600',
                      color: chat.is_author_removed ? '#9CA3AF' : theme.primary,
                      fontStyle: chat.is_author_removed ? 'italic' : 'normal'
                    }}>
                      {chat.is_author_removed ? tCommon('removed_author') : (chat.author_name || chat.author_id)}
                    </span>
                    <span>{formatTimeAgo(chat.created_at, tUi, formattingLocale)}</span>
                    {chat.edited_at && (
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>{t('edited_marker')}</span>
                    )}
                  </div>
                  {renderEngagement(chat.id)}
                </div>

                {/* Root body */}
                <div style={{ padding: '6px 10px 8px', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                  {chat.content}
                </div>

                {/* Action row */}
                <div style={{ padding: '0 10px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => { requireAuth('gate_body_comment', () => { setReplyingTo(chat.id); setReplyBody(''); }); }}
                      style={btnReply}
                    >
                      {t('reply')}
                    </button>
                    <span style={{ fontFamily: 'OpenSauceSans, sans-serif', fontSize: '11px', color: '#9ca3af' }}>
                      {responses.length > 0
                        ? `${responses.length} ${responses.length === 1 ? t('reply') : t('replies')}`
                        : t('no_replies_yet')}
                    </span>
                    {isOwn && (
                      <>
                        <button onClick={() => handleStartEdit(chat)} title={t('edit')} style={iconBtn('#6b7280')}>
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => setDeleteModal({ visible: true, chatId: chat.id })} title={t('delete')} style={iconBtn('#DC2626')}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (!currentUser?.id) { requireAuth('gate_body_generic'); return; }
                      setReportTargetId(chat.id);
                      setShowReportModal(true);
                    }}
                    style={iconBtn('#DC2626')}
                  >
                    <Flag size={11} />
                  </button>
                </div>
              </>
            )}

            {/* Root reply composer */}
            {!isDeleted && !isEditing && (
              <SlideDown open={replyingTo === chat.id}>
                {replyingTo === chat.id && (
                  <div style={{ padding: '8px 10px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                    <textarea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder={t('respond_placeholder')}
                      style={composerTextareaStyle(false)}
                      autoFocus
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                      <button onClick={() => { setReplyingTo(null); setReplyBody(''); }} style={btnSecondary}>{tCommon('cancel')}</button>
                      <button onClick={() => handlePostReply(chat.id, chat.id)} disabled={saving} style={btnConfirm(saving)}>
                        {t('chat:save_exit')}
                      </button>
                    </div>
                  </div>
                )}
              </SlideDown>
            )}

            {/* Responses */}
            {isLoading && (
              <div style={{ padding: '12px 10px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '12px', color: '#9ca3af' }}>
                {'...'}
              </div>
            )}
            {!isLoading && flatResponses.length > 0 && (
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                {flatResponses.map(r => renderMessageBlock(r, chat.id, { indent: r.indentLevel }))}
              </div>
            )}
          </div>
        </SlideDown>
      </div>
    );
  };

  // ======================================================================
  // Flat comment thread (inherited-headline mode — article/event comments)
  // Top-level comment rendered flush; replies indented; everything expanded.
  // ======================================================================
  const renderFlatComment = (comment) => {
    const responses = threadResponses[comment.id] || [];
    const flatResponses = buildFlattenedResponses(responses, comment.id);
    return (
      <div
        key={comment.id}
        ref={el => { threadRefs.current[comment.id] = el; }}
        style={{ borderBottom: '1px solid #eef0f2', paddingBottom: '4px', marginBottom: '4px' }}
      >
        {renderMessageBlock(comment, comment.id, { topLevel: true })}
        {flatResponses.length > 0 && flatResponses.map(r => renderMessageBlock(r, comment.id, { indent: r.indentLevel }))}
      </div>
    );
  };

  // --- Top-level items: exclude orphan gravestones (soft-deleted roots, zero live replies) ---
  const topLevelItems = (data || []).filter(c =>
    !c.parent_chat_id &&
    !c.is_hidden &&
    !(c.is_deleted_by_author && (c.reply_count || 0) === 0)
  );

  // --- New top-level composer (toggle for chat, inline for comment) ---
  const renderTopComposer = () => (
    <SlideDown open={composerOpen}>
      <div style={{
        padding: '10px',
        background: cfg.skin === 'plain' ? 'transparent' : '#f0f7ff',
        borderRadius: '6px',
        marginBottom: '6px',
        border: cfg.skin === 'plain' ? 'none' : `1px solid ${theme.primary}30`,
        borderBottom: cfg.skin === 'plain' ? '1px solid #eef0f2' : `1px solid ${theme.primary}30`
      }}>
        {cfg.requireHeadline && (
          <>
            <input
              type="text"
              value={newHeadline}
              onChange={(e) => { setNewHeadline(e.target.value); setComposerErrors(prev => ({ ...prev, headline: null })); }}
              placeholder={t('placeholder_headline')}
              maxLength={300}
              style={{
                width: '100%', padding: '6px 8px', fontSize: '13px', fontFamily: 'OpenSauceSans, sans-serif',
                border: composerErrors.headline ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px',
                outline: 'none', marginBottom: composerErrors.headline ? '2px' : '6px', boxSizing: 'border-box'
              }}
            />
            {composerErrors.headline && (
              <div style={{ fontSize: '11px', color: '#EF4444', marginBottom: '4px' }}>{composerErrors.headline}</div>
            )}
          </>
        )}
        <textarea
          value={newBody}
          onChange={(e) => { setNewBody(e.target.value); setComposerErrors(prev => ({ ...prev, body: null })); }}
          placeholder={cfg.requireHeadline ? t('placeholder_content') : t('placeholder_comment')}
          maxLength={5000}
          onFocus={(e) => { if (!isLoggedIn) { e.target.blur(); requireAuth('gate_body_comment'); } }}
          style={{
            width: '100%', minHeight: '60px', padding: '6px 8px', fontSize: '13px', fontFamily: 'OpenSauceSans, sans-serif',
            border: composerErrors.body ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px',
            outline: 'none', resize: 'vertical', marginBottom: composerErrors.body ? '2px' : '6px', boxSizing: 'border-box'
          }}
        />
        {composerErrors.body && (
          <div style={{ fontSize: '11px', color: '#EF4444', marginBottom: '4px' }}>{composerErrors.body}</div>
        )}
        <div style={{ display: 'flex', justifyContent: cfg.composerMode === 'toggle' ? 'space-between' : 'flex-end' }}>
          {cfg.composerMode === 'toggle' && (
            <button
              onClick={() => { setComposerOpen(false); setNewHeadline(''); setNewBody(''); setComposerErrors({}); }}
              style={btnSecondaryWide}
            >
              {tCommon('cancel')}
            </button>
          )}
          <button onClick={handleCreateTopLevel} disabled={saving} style={btnPrimaryWide(theme, saving)}>
            {cfg.requireHeadline ? t('chat:save_exit') : t('post_comment')}
          </button>
        </div>
      </div>
    </SlideDown>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0,
      maxWidth: cfg.maxWidth || 'none', width: '100%'
    }}>
      {/* New-item composer */}
      {renderTopComposer()}

      {/* Scrollable list */}
      <div
        ref={scrollContainerRef}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', paddingRight: '2px' }}
      >
        {topLevelItems.length === 0 && !(cfg.composerMode === 'toggle' && composerOpen) && (
          <div style={{ padding: '32px 16px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>
            {emptyState}
          </div>
        )}
        {topLevelItems.map(item => cfg.collapsible ? renderCollapsibleThread(item) : renderFlatComment(item))}
      </div>

      {/* Modals */}
      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('notification')}
        message={modal.message}
        onOk={() => { try { modal.onOk?.(); } catch (e) { setModal({ visible: false, message: '', onOk: null }); } }}
        onCancel={() => setModal({ visible: false, message: '', onOk: null })}
      />
      <ReportModal
        visible={showReportModal}
        contentType={reportContentType}
        contentId={reportTargetId}
        onClose={() => { setShowReportModal(false); setReportTargetId(null); }}
        onSubmit={async (reportData) => {
          const result = await userActionService.submitReport(currentUser?.id, reportData);
          if (result.success) {
            setModal({ visible: true, message: tCommon('report_submitted'), onOk: () => setModal({ visible: false, message: '', onOk: null }) });
            setShowReportModal(false);
            setReportTargetId(null);
          }
        }}
      />
      <ConfirmationModal
        visible={deleteModal.visible}
        title={t('delete_message_title')}
        message=""
        cancelLabel={tCommon('cancel')}
        okLabel={t('delete')}
        danger
        onCancel={() => setDeleteModal({ visible: false, chatId: null })}
        onOk={handleConfirmDelete}
      />
      <ConfirmationModal
        visible={discardModal.visible}
        title={t('discard_reply_title')}
        message=""
        cancelLabel={t('keep_editing')}
        okLabel={t('discard')}
        danger
        onCancel={() => {
          setDiscardModal({ visible: false, pendingAction: null });
          if (openThreadId && threadRefs.current[openThreadId]) {
            const textarea = threadRefs.current[openThreadId].querySelector('textarea');
            if (textarea) setTimeout(() => textarea.focus(), 50);
          }
        }}
        onOk={() => {
          const action = discardModal.pendingAction;
          setDiscardModal({ visible: false, pendingAction: null });
          action?.();
        }}
      />
    </div>
  );
};

// --- Shared inline style helpers ---
const metaLineStyle = {
  fontFamily: 'OpenSauceSans, sans-serif', fontSize: '11px', color: '#6b7280', marginBottom: '2px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
};
const composerTextareaStyle = () => ({
  width: '100%', minHeight: '50px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 8px',
  fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box'
});
const btnReply = {
  background: '#10B981', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 8px',
  fontSize: '11px', fontWeight: '600', fontFamily: 'OpenSauceSans, sans-serif', cursor: 'pointer'
};
const btnSecondary = {
  background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', padding: '3px 10px',
  fontSize: '12px', fontWeight: '600', fontFamily: 'OpenSauceSans, sans-serif', cursor: 'pointer'
};
const btnSecondaryWide = { ...btnSecondary, padding: '4px 12px' };
const btnConfirm = (saving) => ({
  background: '#10B981', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px',
  fontSize: '12px', fontWeight: '600', fontFamily: 'OpenSauceSans, sans-serif',
  cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1
});
const btnPrimaryWide = (theme, saving) => ({
  background: theme.primary, color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px',
  fontSize: '12px', fontWeight: '600', fontFamily: 'OpenSauceSans, sans-serif',
  cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1
});
const iconBtn = (color) => ({
  background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px',
  display: 'inline-flex', alignItems: 'center', color
});

export default ConcertinaThread;
